import { useEffect, useState, type FormEvent } from 'react';
import parse from 'html-react-parser';
import DOMPurify from 'dompurify';
import { FileText, LoaderCircle, MessageSquare, Send } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { API_BASE, fetchJson } from '../lib/api';
import { PrivacyChoicesButton } from '../components/consent/PrivacyChoicesButton';

interface PublicFeedbackComment {
  id: string;
  body: string;
  author: {
    email: string | null;
    name: string | null;
  };
  createdAt: string;
}

interface PublicFeedbackAttachment {
  id: string;
  filename: string;
  sizeBytes: number;
  url: string;
}

interface PublicFeedbackItem {
  _id: string;
  publicId: string | null;
  title: string;
  description: string;
  status: string;
  attachments: PublicFeedbackAttachment[];
  comments: PublicFeedbackComment[];
  /** Comments on the thread, not just the ones in this response. */
  commentTotal: number;
  /** Opaque cursor for the next page of comments, or null when complete. */
  commentsNextCursor: string | null;
  createdAt: string;
}

function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function formatStatus(status: string): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase());
}

export function PublicFeedbackPage() {
  const { projectId, token } = useParams<{ projectId: string; token: string }>();
  const [item, setItem] = useState<PublicFeedbackItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [commentBody, setCommentBody] = useState('');
  const [commentError, setCommentError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [loadingMoreComments, setLoadingMoreComments] = useState(false);

  // The item response carries only the first page of comments. Following the
  // cursor keeps the rest of the thread reachable.
  const loadMoreComments = async () => {
    if (!projectId || !token || !item?.commentsNextCursor || loadingMoreComments) {
      return;
    }

    setLoadingMoreComments(true);
    try {
      const payload = await fetchJson<{ item: PublicFeedbackItem }>(
        `/api/public/feedback/${encodeURIComponent(projectId)}/${encodeURIComponent(token)}?commentCursor=${encodeURIComponent(item.commentsNextCursor)}`
      );
      setItem((current) =>
        current
          ? {
              ...current,
              comments: [...current.comments, ...payload.item.comments],
              commentsNextCursor: payload.item.commentsNextCursor,
            }
          : current
      );
    } catch (error: unknown) {
      setCommentError(error instanceof Error ? error.message : 'Unable to load more comments.');
    } finally {
      setLoadingMoreComments(false);
    }
  };

  useEffect(() => {
    if (!projectId || !token) {
      setLoadError('Feedback item not found.');
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    fetchJson<{ item: PublicFeedbackItem }>(
      `/api/public/feedback/${encodeURIComponent(projectId)}/${encodeURIComponent(token)}`
    )
      .then((payload) => {
        if (!cancelled) {
          setItem(payload.item);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setLoadError(error instanceof Error ? error.message : 'Unable to load feedback.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [projectId, token]);

  async function handleSubmitComment(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const body = commentBody.trim();
    if (!body || !projectId || !token) {
      return;
    }

    setSubmitting(true);
    setCommentError(null);
    try {
      const payload = await fetchJson<{ comment: PublicFeedbackComment }>(
        `/api/public/feedback/${encodeURIComponent(projectId)}/${encodeURIComponent(token)}/comments`,
        null,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ body }),
        }
      );
      setItem((current) =>
        current ? { ...current, comments: [...current.comments, payload.comment] } : current
      );
      setCommentBody('');
    } catch (error) {
      setCommentError(error instanceof Error ? error.message : 'Unable to post comment.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="min-h-screen bg-slate-950 text-slate-100">
      <header className="border-b border-slate-800 bg-slate-950/95">
        <div className="mx-auto flex w-full max-w-4xl items-center justify-between px-5 py-4 sm:px-8">
          <span className="text-base font-semibold text-white">Perspectify</span>
          {item ? (
            <span className="rounded-md border border-slate-700 bg-slate-900 px-2.5 py-1 text-xs font-medium text-slate-300">
              {formatStatus(item.status)}
            </span>
          ) : null}
        </div>
      </header>

      <div className="mx-auto w-full max-w-4xl px-5 py-10 sm:px-8 sm:py-14">
        {loading ? (
          <div className="flex min-h-64 items-center justify-center text-slate-400">
            <LoaderCircle className="h-6 w-6 animate-spin" aria-label="Loading feedback" />
          </div>
        ) : loadError || !item ? (
          <section className="border-l-2 border-rose-400 pl-5">
            <h1 className="text-2xl font-semibold text-white">Feedback unavailable</h1>
            <p className="mt-3 text-sm text-slate-400">{loadError ?? 'Feedback item not found.'}</p>
          </section>
        ) : (
          <div className="space-y-10">
            <section>
              <p className="text-sm font-medium text-cyan-300">
                {item.publicId ?? item._id.slice(0, 8)}
              </p>
              <h1 className="mt-2 text-3xl font-semibold text-white sm:text-4xl">{item.title}</h1>
              <p className="mt-3 text-sm text-slate-500">
                Submitted {new Date(item.createdAt).toLocaleString()}
              </p>
              {item.description ? (
                <div className="prose prose-invert mt-7 max-w-none text-slate-300">
                  {parse(DOMPurify.sanitize(item.description))}
                </div>
              ) : null}
            </section>

            {item.attachments.length > 0 ? (
              <section className="border-t border-slate-800 pt-8">
                <h2 className="flex items-center gap-2 text-base font-semibold text-white">
                  <FileText className="h-4 w-4 text-cyan-300" aria-hidden="true" />
                  Attachments
                </h2>
                <div className="mt-4 divide-y divide-slate-800 border-y border-slate-800">
                  {item.attachments.map((attachment) => (
                    <a
                      key={attachment.id}
                      href={`${API_BASE}${attachment.url}`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center justify-between gap-4 py-3 text-sm text-slate-200 hover:text-cyan-300"
                    >
                      <span className="min-w-0 truncate">{attachment.filename}</span>
                      <span className="shrink-0 text-xs text-slate-500">
                        {formatFileSize(attachment.sizeBytes)}
                      </span>
                    </a>
                  ))}
                </div>
              </section>
            ) : null}

            <section className="border-t border-slate-800 pt-8">
              <h2 className="flex items-center gap-2 text-base font-semibold text-white">
                <MessageSquare className="h-4 w-4 text-cyan-300" aria-hidden="true" />
                Conversation
              </h2>

              {item.commentsNextCursor ? (
                <div className="mt-5 flex justify-center">
                  <button
                    type="button"
                    onClick={() => void loadMoreComments()}
                    disabled={loadingMoreComments}
                    className="inline-flex items-center gap-2 rounded-md border border-slate-700 px-3 py-2 text-xs font-medium text-slate-300 transition hover:border-slate-500 hover:text-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {loadingMoreComments
                      ? 'Loading…'
                      : `Load earlier comments (${item.comments.length} of ${item.commentTotal})`}
                  </button>
                </div>
              ) : null}

              {item.comments.length > 0 ? (
                <div className="mt-5 divide-y divide-slate-800 border-y border-slate-800">
                  {item.comments.map((comment) => (
                    <article key={comment.id} className="py-5">
                      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
                        <span>{comment.author.name || comment.author.email || 'Reporter'}</span>
                        <time dateTime={comment.createdAt}>
                          {new Date(comment.createdAt).toLocaleString()}
                        </time>
                      </div>
                      <div className="prose prose-invert mt-3 max-w-none text-sm text-slate-300">
                        {parse(DOMPurify.sanitize(comment.body))}
                      </div>
                    </article>
                  ))}
                </div>
              ) : null}

              <form onSubmit={handleSubmitComment} className="mt-6">
                <label htmlFor="public-feedback-comment" className="text-sm font-medium text-slate-200">
                  Add a comment
                </label>
                <textarea
                  id="public-feedback-comment"
                  value={commentBody}
                  onChange={(event) => setCommentBody(event.target.value)}
                  rows={5}
                  className="mt-2 w-full resize-y rounded-md border border-slate-700 bg-slate-900 px-3 py-3 text-sm text-white outline-none transition placeholder:text-slate-600 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20"
                  placeholder="Write a comment…"
                  disabled={submitting}
                />
                {commentError ? (
                  <p className="mt-2 text-sm text-rose-300">{commentError}</p>
                ) : null}
                <div className="mt-3 flex justify-end">
                  <button
                    type="submit"
                    disabled={submitting || !commentBody.trim()}
                    className="inline-flex items-center gap-2 rounded-md bg-cyan-400 px-4 py-2 text-sm font-semibold text-slate-950 transition hover:bg-cyan-300 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {submitting ? (
                      <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />
                    ) : (
                      <Send className="h-4 w-4" aria-hidden="true" />
                    )}
                    Post comment
                  </button>
                </div>
              </form>
            </section>
          </div>
        )}
        <footer className="mt-10 flex justify-center">
          <PrivacyChoicesButton variant="link" />
        </footer>
      </div>
    </main>
  );
}
